import PropTypes from 'prop-types';
import styled from 'styled-components';
import type { ResponsiveValue } from 'styled-system';
import { variant } from 'styled-system';

import type { CommonProps, TypographyProps } from './constants';
import { COMMON, TYPOGRAPHY } from './constants';
import type { Theme } from './theme';
import theme from './theme';

type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type Props = CommonProps &
  TypographyProps & { as?: keyof JSX.IntrinsicElements | React.ComponentType<any> } & {
    variant?: ResponsiveValue<Variant, Theme>;
  };

// eslint-disable-next-line @typescript-eslint/ban-types
const headingStyle = variant<object, Variant>({
  variants: {
    h1: {
      fontSize: `${Math.pow(1.25, 4) * 0.8}rem`,
      fontWeight: 'bold',
      lineHeight: 1.2,
      [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: `${Math.pow(1.25, 4)}rem`,
      },
    },
    h2: {
      fontSize: `${Math.pow(1.25, 3) * 0.8}rem`,
      lineHeight: 1.408,
      [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: `${Math.pow(1.25, 3)}rem`,
      },
    },
    h3: {
      fontSize: `${Math.pow(1.25, 2) * 0.8}rem`,
      fontWeight: 'bold',
      lineHeight: 1.2,
      [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: `${Math.pow(1.25, 2)}rem`,
      },
    },
    h4: {
      fontSize: `${Math.pow(1.25, 1) * 0.9}rem`,
      fontWeight: 'bold',
      lineHeight: 1.2,
      [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: `${Math.pow(1.25, 1)}rem`,
      },
    },
    h5: {
      color: theme.colors.gray.dark,
      letterSpacing: 1.872,
      textTransform: 'uppercase',
      fontSize: `${Math.pow(1.25, -1)}rem`,
      fontWeight: 'bold',
      lineHeight: 1.1,
    },
    h6: {
      fontSize: theme.fontSizes.m,
      fontWeight: 'bold',
      lineHeight: 1.25,
    },
  },
});

const Heading = styled.h1<Props>`
  ${TYPOGRAPHY} ${COMMON} ${headingStyle};
`;

Heading.defaultProps = {
  mt: 0,
  mb: 0,
  ml: 0,
  mr: 0,
  theme,
};

Heading.propTypes = {
  ...TYPOGRAPHY.propTypes,
  ...COMMON.propTypes,
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  variant: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

Heading.displayName = 'Heading';

export { Heading };
